<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed, ref } from 'vue';

import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import EtaReportTableFold from '@/components/tableFolds/EtaReportTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import { useUserStore } from '@/stores/useUserStore';
import * as dateHelpers from '@/helpers/date';
import { ETAReportResponse } from '@/models/reports';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<ETAReportResponse[]>;
  date?: Date;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  // TODO maybe don't show data when date is undefined
  date: undefined,
});

const userStore = useUserStore();

const openFoldKey = ref('');

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<ETAReportResponse[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'job_id',
      title: 'Job ID',
      colspan: 2,
      isNotSortable: true,
    },
    {
      value: 'cost_service',
      title: 'Cost service',
      isNotSortable: true,
    },
    {
      value: 'ref1',
      title: 'Reference 1',
      isNotSortable: true,
    },
    {
      value: 'name',
      title: 'Stop name',
    },
    {
      value: 'suburb',
      title: 'Suburb',
    },
    {
      value: 'state',
      title: 'State',
      isNotSortable: true,
    },
    {
      value: 'eta',
      title: 'ETA',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleClick(dataItemUniqueKey: string): void {
  if (openFoldKey.value === dataItemUniqueKey) {
    openFoldKey.value = '';
  } else {
    openFoldKey.value = dataItemUniqueKey;
  }
}

const userTimeZone = computed(() => userStore.user.timezone);
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.stickyHeader,
      $style.etaReportTable,
    ]"
    data-testid="etaReportTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.unique_key">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.unique_key },
          ]"
        >
          <td class="whitespace-nowrap">
            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.unique_key"
              @click="handleClick(dataItem.unique_key)"
            />
          </td>
          <td>
            <JobIdAndIconLinkUnit
              :job-id="dataItem.job_id"
              :job-date="
                dateHelpers.getApiZonedFormattedDate(
                  // TODO not shure about TZ here, maybe need: `formatInTimeZone(props.date, userTimeZone, 'yyyy-MM-dd')`
                  // TODO maybe don't show data when date is undefined
                  // @ts-expect-error
                  props.date,
                  userTimeZone,
                )
              "
            />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.cost_service" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.ref1" />
          </td>
          <td>
            <EmDashPlaceholder :item="dataItem.name" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.suburb" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.state" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.eta" />
          </td>
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.unique_key },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.unique_key">
              <!-- TODO readonly descendant props -->
              <!-- @vue-ignore -->
              <EtaReportTableFold :packages="dataItem.packages" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.etaReportTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
</style>
